/**
 * FamilyJoinPage
 *
 * Handles the /join?code=XXX route for family invite links.
 * - Extracts the invite code from URL query parameters
 * - If user is not authenticated, redirects to login with return URL preserved
 * - If authenticated, calls POST /family/join with the code
 * - Shows loading, success, and error states
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useStatus } from '../contexts/StatusContext';
import { Make_Authorized_API_Call, API_ENDPOINTS, isSuccessResponse } from '../api/apiClient';

type JoinState = 'idle' | 'joining' | 'success' | 'error' | 'needs-login';

interface JoinResult {
  family_name?: string;
  user_name?: string;
}

const FamilyJoinPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { Auth_Status, Is_Loading: authLoading } = useStatus();

  const codeFromUrl = searchParams.get('code') || '';

  const [familyCode, setFamilyCode] = useState<string>(codeFromUrl);
  const [joinState, setJoinState] = useState<JoinState>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [joinResult, setJoinResult] = useState<JoinResult>({});

  // Redirect unauthenticated users to login, preserving the invite code
  useEffect(() => {
    if (authLoading) return;

    if (!Auth_Status) {
      setJoinState('needs-login');
    }
  }, [Auth_Status, authLoading]);

  const handleJoinFamily = useCallback(async (code: string) => {
    const trimmedCode = code.trim().toUpperCase();

    if (!trimmedCode) {
      setErrorMessage('Please enter a family code.');
      setJoinState('error');
      return;
    }

    setJoinState('joining');
    setErrorMessage('');

    try {
      const response = await Make_Authorized_API_Call(
        API_ENDPOINTS.FAMILY.JOIN,
        'POST',
        { family_code: trimmedCode }
      );

      if (isSuccessResponse(response)) {
        const record = response.data?.Records?.[0] || {};
        setJoinResult({
          family_name: record.family_name,
          user_name: record.user_name,
        });
        setJoinState('success');
      } else {
        // Handle specific backend error codes
        const message = response.message || 'Failed to join family. Please check the code and try again.';
        setErrorMessage(message);
        setJoinState('error');
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'A network error occurred. Please try again.');
      setJoinState('error');
    }
  }, []);

  // Auto-join if code is present in URL and user is authenticated
  useEffect(() => {
    if (authLoading) return;
    if (!Auth_Status) return;
    if (!codeFromUrl) return;
    if (joinState !== 'idle') return;

    handleJoinFamily(codeFromUrl);
  }, [Auth_Status, authLoading, codeFromUrl, joinState, handleJoinFamily]);

  const handleLoginRedirect = () => {
    // Store the full join URL so user returns here after login
    const returnUrl = `/join${codeFromUrl ? `?code=${encodeURIComponent(codeFromUrl)}` : ''}`;
    sessionStorage.setItem('postLoginRedirect', returnUrl);
    navigate('/');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleJoinFamily(familyCode);
  };

  const handleGoToDashboard = () => {
    navigate('/tasks');
  };

  // Loading state while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Join a Family</h1>
          <p className="text-gray-500 mt-1">Enter your family code to get started</p>
        </div>

        {/* Needs Login State */}
        {joinState === 'needs-login' && (
          <div className="text-center">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <svg className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div className="text-left">
                  <h3 className="text-sm font-semibold text-amber-800">Sign in required</h3>
                  <p className="text-sm text-amber-700 mt-1">
                    You need to sign in or create an account before joining a family.
                    {codeFromUrl && ' Your invite code will be saved.'}
                  </p>
                </div>
              </div>
            </div>
            <button
              onClick={handleLoginRedirect}
              className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all shadow-lg"
            >
              Sign In or Create Account
            </button>
          </div>
        )}

        {/* Joining State */}
        {joinState === 'joining' && (
          <div className="text-center py-8">
            <div className="animate-spin w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-600 text-lg">Joining family...</p>
            <p className="text-gray-400 text-sm mt-1">This will only take a moment</p>
          </div>
        )}

        {/* Success State */}
        {joinState === 'success' && (
          <div className="text-center">
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
              <svg className="w-12 h-12 text-green-600 mx-auto mb-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <h3 className="text-lg font-bold text-green-800 mb-1">Welcome to the family!</h3>
              {joinResult.family_name && (
                <p className="text-green-700">
                  You have successfully joined <strong>{joinResult.family_name}</strong>.
                </p>
              )}
            </div>
            <button
              onClick={handleGoToDashboard}
              className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all shadow-lg"
            >
              Go to Dashboard
            </button>
          </div>
        )}

        {/* Error State */}
        {joinState === 'error' && (
          <div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <svg className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div>
                  <h3 className="text-sm font-semibold text-red-800">Unable to join family</h3>
                  <p className="text-sm text-red-700 mt-1">{errorMessage}</p>
                </div>
              </div>
            </div>

            {/* Show manual entry form after error */}
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Family Code
                </label>
                <input
                  type="text"
                  value={familyCode}
                  onChange={(e) => setFamilyCode(e.target.value.toUpperCase())}
                  maxLength={6}
                  placeholder="ABC123"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-center text-2xl font-bold tracking-widest uppercase"
                  autoFocus
                />
                <p className="text-xs text-gray-500 mt-1">Enter the 6-character code from your invite</p>
              </div>
              <button
                type="submit"
                disabled={!familyCode.trim()}
                className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Try Again
              </button>
            </form>
          </div>
        )}

        {/* Idle State - manual code entry */}
        {joinState === 'idle' && Auth_Status && (
          <form onSubmit={handleSubmit}>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Family Code
              </label>
              <input
                type="text"
                value={familyCode}
                onChange={(e) => setFamilyCode(e.target.value.toUpperCase())}
                maxLength={6}
                placeholder="ABC123"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-center text-2xl font-bold tracking-widest uppercase"
                autoFocus
              />
              <p className="text-xs text-gray-500 mt-1">Enter the 6-character code from your invite</p>
            </div>
            <button
              type="submit"
              disabled={!familyCode.trim()}
              className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Join Family
            </button>
          </form>
        )}

        {/* Back to home link */}
        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/')}
            className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            Back to home
          </button>
        </div>
      </div>
    </div>
  );
};

export default FamilyJoinPage;
